import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import {
    Box, Typography, CircularProgress, Alert, Button, ButtonGroup,
    Paper, Divider, Grid, Chip, useTheme
} from "@mui/material";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { useChatbots } from "../../../contexts/ChatbotContext";

Chart.register(...registerables);

const CHART_COLORS = [
    '#42a5f5', '#ab47bc', '#26c6da', '#66bb6a',
    '#ffa726', '#ef5350', '#8d6e63', '#78909c',
    '#5c6bc0', '#26a69a', '#d4e157', '#ec407a'
];

// Convert breakdown object { key: {count, ...} } to sorted array
const toSortedArray = (obj) => {
    if (!obj || typeof obj !== 'object') return [];
    return Object.entries(obj)
        .map(([key, val]) => ({ name: key, ...val }))
        .sort((a, b) => (b.count || 0) - (a.count || 0));
};

export default function LlmCallLogCharts() {
    const { activeChatbot } = useChatbots();
    const theme = useTheme();
    const { chatbotId: paramsChatbotId } = useParams();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [statsData, setStatsData] = useState(null);
    const [costRangeData, setCostRangeData] = useState(null);
    const [chatbotStats, setChatbotStats] = useState(null);
    const [dayRange, setDayRange] = useState(30);

    useEffect(() => {
        if (activeChatbot) fetchAllData();
        else {
            setStatsData(null);
            setCostRangeData(null);
            setChatbotStats(null);
        }
    }, [activeChatbot, dayRange]);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);

        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - dayRange);
        const fmt = (d) => d.toISOString().split('T')[0];

        const requests = [
            axiosInstance.get('/api/llm-logs/stats', { params: { days: dayRange, team_view: false } }),
            axiosInstance.get('/api/llm-logs/cost-range', { params: { start_date: fmt(start), end_date: fmt(end), team_view: false } })
        ];

        if (paramsChatbotId) {
            requests.push(
                axiosInstance.get(`/api/llm-logs/chatbot/${paramsChatbotId}/stats`, { params: { days: dayRange } })
            );
        }

        try {
            const results = await Promise.allSettled(requests);

            if (results[0].status === 'fulfilled') setStatsData(results[0].value.data.data);
            if (results[1].status === 'fulfilled') setCostRangeData(results[1].value.data.data);
            if (results[2] && results[2].status === 'fulfilled') setChatbotStats(results[2].value.data.data);

            if (results[0].status === 'rejected' && results[1].status === 'rejected') {
                setError("Failed to fetch LLM call log data.");
            }
        } catch {
            setError("Failed to fetch LLM call log data.");
        } finally {
            setLoading(false);
        }
    };

    // ---- Chart Data Builders ----

    const buildCostTrendData = () => {
        const breakdown = costRangeData?.daily_breakdown;
        if (!breakdown || typeof breakdown !== 'object') return null;
        const entries = Object.entries(breakdown).sort(([a], [b]) => a.localeCompare(b));
        if (!entries.length) return null;
        return {
            labels: entries.map(([date]) => date),
            datasets: [
                {
                    label: 'Cost ($)',
                    data: entries.map(([, v]) => v.cost || 0),
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.light + '40',
                    yAxisID: 'y', tension: 0.3, fill: true
                },
                {
                    label: 'Calls',
                    data: entries.map(([, v]) => v.calls || 0),
                    borderColor: theme.palette.secondary.main,
                    backgroundColor: 'transparent',
                    yAxisID: 'y1', tension: 0.3, fill: false, borderDash: [5, 5]
                }
            ]
        };
    };

    const buildPurposeCountData = () => {
        const arr = toSortedArray(statsData?.purpose_breakdown);
        if (!arr.length) return null;
        return {
            labels: arr.map(p => p.name.replace(/_/g, ' ')),
            datasets: [{
                data: arr.map(p => p.count),
                backgroundColor: arr.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
                borderWidth: 1
            }]
        };
    };

    const buildPurposeCostData = () => {
        const arr = toSortedArray(statsData?.purpose_breakdown);
        if (!arr.length) return null;
        return {
            labels: arr.map(p => p.name.replace(/_/g, ' ')),
            datasets: [
                {
                    label: 'Input Cost ($)',
                    data: arr.map(p => p.input_cost || 0),
                    backgroundColor: theme.palette.primary.light
                },
                {
                    label: 'Output Cost ($)',
                    data: arr.map(p => p.output_cost || 0),
                    backgroundColor: theme.palette.primary.main
                }
            ]
        };
    };

    const buildModelTokensData = () => {
        const arr = toSortedArray(statsData?.model_breakdown);
        if (!arr.length) return null;
        return {
            labels: arr.map(m => m.name),
            datasets: [
                {
                    label: 'Input Tokens',
                    data: arr.map(m => m.input_tokens || 0),
                    backgroundColor: theme.palette.primary.light
                },
                {
                    label: 'Output Tokens',
                    data: arr.map(m => m.output_tokens || 0),
                    backgroundColor: theme.palette.primary.main
                }
            ]
        };
    };

    const buildModelCostData = () => {
        const arr = toSortedArray(statsData?.model_breakdown);
        if (!arr.length) return null;
        return {
            labels: arr.map(m => m.name),
            datasets: [
                {
                    label: 'Calls',
                    data: arr.map(m => m.count),
                    backgroundColor: theme.palette.info.light
                },
                {
                    label: 'Cost ($)',
                    data: arr.map(m => m.total_cost || 0),
                    backgroundColor: theme.palette.info.main
                }
            ]
        };
    };

    const buildProviderData = () => {
        const arr = toSortedArray(statsData?.provider_breakdown);
        if (!arr.length) return null;
        return {
            labels: arr.map(p => p.name),
            datasets: [{
                data: arr.map(p => p.count),
                backgroundColor: arr.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
                borderWidth: 1
            }]
        };
    };

    const buildRefTypeData = () => {
        const arr = toSortedArray(statsData?.reference_type_breakdown);
        if (!arr.length) return null;
        return {
            labels: arr.map(r => r.name.replace(/_/g, ' ')),
            datasets: [{
                data: arr.map(r => r.count),
                backgroundColor: arr.map((_, i) => CHART_COLORS[(i + 4) % CHART_COLORS.length]),
                borderWidth: 1
            }]
        };
    };

    // ---- Chart Options ----

    const costTrendOptions = {
        responsive: true,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { display: true } },
        scales: {
            x: { title: { display: true, text: 'Date' } },
            y: { type: 'linear', position: 'left', title: { display: true, text: 'Cost ($)' } },
            y1: { type: 'linear', position: 'right', title: { display: true, text: 'Calls' }, grid: { drawOnChartArea: false } }
        }
    };

    const doughnutOptions = {
        responsive: true,
        plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, padding: 8 } } }
    };

    const horizontalBarOptions = {
        indexAxis: 'y', responsive: true,
        plugins: { legend: { display: true } }
    };

    const stackedBarOptions = {
        responsive: true,
        plugins: { legend: { display: true } },
        scales: { x: { stacked: true }, y: { stacked: true } }
    };

    // ---- Metric Card ----
    const cardStyle = {
        p: 2, borderRadius: 2, bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`, textAlign: 'center'
    };

    const MetricCard = ({ label, value, color }) => (
        <Paper sx={cardStyle}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: color || 'text.primary' }}>
                {value}
            </Typography>
        </Paper>
    );

    // ---- Render ----
    const s = statsData;

    return (
        <Paper sx={{ p: 3, bgcolor: "background.paper" }}>
            <Typography variant="h5" color="text.primary" gutterBottom>
                LLM Call Log Analytics
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <ButtonGroup variant="contained" size="small" sx={{ mb: 3 }}>
                {[7, 14, 30, 90].map(d => (
                    <Button key={d} onClick={() => setDayRange(d)}
                        color={dayRange === d ? "primary" : "inherit"}>
                        {d} Days
                    </Button>
                ))}
            </ButtonGroup>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            )}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!loading && !error && s && (
                <>
                    {/* Chatbot-specific summary */}
                    {chatbotStats && (
                        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                            <Typography variant="subtitle2">
                                Chatbot Stats (last {dayRange} days):&nbsp;
                                <strong>{chatbotStats.total_calls || 0}</strong> calls&nbsp;|&nbsp;
                                <strong>{(chatbotStats.total_tokens || 0).toLocaleString()}</strong> tokens&nbsp;|&nbsp;
                                <strong>${(chatbotStats.total_cost || 0).toFixed(4)}</strong> cost
                            </Typography>
                        </Paper>
                    )}

                    {/* Row 1: 8 Summary Metric Cards */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={6} sm={3}>
                            <MetricCard label="Total Calls" value={(s.total_calls || 0).toLocaleString()} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard label="Successful" value={(s.successful_calls || 0).toLocaleString()} color="success.main" />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard label="Failed" value={(s.failed_calls || 0).toLocaleString()} color="error.main" />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard label="Success Rate" value={s.success_rate != null ? `${s.success_rate}%` : '—'} color="success.main" />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard label="Total Tokens" value={(s.tokens?.total || 0).toLocaleString()} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard label="Input Tokens" value={(s.tokens?.input || 0).toLocaleString()} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard label="Output Tokens" value={(s.tokens?.output || 0).toLocaleString()} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard label="Avg Response" value={s.avg_response_time_ms != null ? `${(s.avg_response_time_ms / 1000).toFixed(1)}s` : '—'} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard label="Total Cost" value={`$${(s.cost?.total || 0).toFixed(4)}`} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard label="Input Cost" value={`$${(s.cost?.input || 0).toFixed(4)}`} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard label="Output Cost" value={`$${(s.cost?.output || 0).toFixed(4)}`} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard label="Period" value={`${s.period_days} days`} />
                        </Grid>
                    </Grid>

                    {/* Row 2: Daily Cost & Call Volume (Line Chart) */}
                    {buildCostTrendData() && (
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom>Daily Cost & Call Volume</Typography>
                            <Line data={buildCostTrendData()} options={costTrendOptions} />
                        </Box>
                    )}

                    {/* Row 3: Purpose Count (Doughnut) + Provider (Doughnut) + Reference Type (Doughnut) */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {buildPurposeCountData() && (
                            <Grid item xs={12} md={4}>
                                <Typography variant="h6" gutterBottom>Calls by Purpose</Typography>
                                <Doughnut data={buildPurposeCountData()} options={doughnutOptions} />
                            </Grid>
                        )}
                        {buildProviderData() && (
                            <Grid item xs={12} md={4}>
                                <Typography variant="h6" gutterBottom>Calls by Provider</Typography>
                                <Doughnut data={buildProviderData()} options={doughnutOptions} />
                            </Grid>
                        )}
                        {buildRefTypeData() && (
                            <Grid item xs={12} md={4}>
                                <Typography variant="h6" gutterBottom>Calls by Reference Type</Typography>
                                <Doughnut data={buildRefTypeData()} options={doughnutOptions} />
                            </Grid>
                        )}
                    </Grid>

                    {/* Row 4: Cost by Purpose (Stacked Bar) */}
                    {buildPurposeCostData() && (
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom>Cost by Purpose (Input vs Output)</Typography>
                            <Bar data={buildPurposeCostData()} options={stackedBarOptions} />
                        </Box>
                    )}

                    {/* Row 5: Model Usage (Horizontal Bar) + Model Tokens (Stacked Bar) */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {buildModelCostData() && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>Calls & Cost by Model</Typography>
                                <Bar data={buildModelCostData()} options={horizontalBarOptions} />
                            </Grid>
                        )}
                        {buildModelTokensData() && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>Tokens by Model (Input vs Output)</Typography>
                                <Bar data={buildModelTokensData()} options={stackedBarOptions} />
                            </Grid>
                        )}
                    </Grid>

                </>
            )}

            {!loading && !error && !s && (
                <Typography color="text.primary">No LLM call log data available.</Typography>
            )}
        </Paper>
    );
}
